// FieldRow.tsx
// Phase 5 (2026-05-25) — renders a single onboarding field based on its
// effective state for the current user.
// Hard rules:
//   - hidden  → return null (does not appear in DOM)
//   - disabled → render as a non-interactive locked row with LOCKED_FIELD_COPY
//   - optional → render with "(optional)" suffix on the label
//   - enabled  → render normally
import React from 'react';
import { Lock } from 'lucide-react';
import {
  FieldSpec, FieldState, LOCKED_FIELD_COPY, fieldStateFor,
} from '../lib/onboardingFields';
import type { UserStatus } from '../hooks/useAuthenticatedUser';

interface Props {
  field: FieldSpec;
  status: UserStatus;
  /** Optional onChange — caller provides; ignored for disabled/hidden states. */
  value?: string;
  onChange?: (next: string) => void;
}

export function FieldRow({ field, status, value, onChange }: Props) {
  const state: FieldState = fieldStateFor(field, status);
  if (state === 'hidden') return null;

  const testidBase = `field-${field.id}`;

  if (state === 'disabled') {
    return (
      <div
        className="rounded-xl border border-dashed border-[var(--input-border)] bg-[var(--input-bg)] p-3 opacity-70 cursor-not-allowed"
        data-testid={`${testidBase}-disabled`}
        aria-disabled="true"
      >
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-2">
            <Lock size={12} className="text-[var(--muted-text)]" />
            <span className="text-xs font-black uppercase tracking-tight italic text-[var(--muted-text)]">{field.label}</span>
          </div>
          <span className="text-[10px] font-black uppercase tracking-widest text-[var(--muted-text)]">Locked</span>
        </div>
        <div className="text-[11px] font-bold text-[var(--muted-text)]" data-testid={`${testidBase}-locked-copy`}>
          {LOCKED_FIELD_COPY}
        </div>
      </div>
    );
  }

  const label = state === 'optional' ? `${field.label} (optional)` : field.label;
  return (
    <label className="block" data-testid={`${testidBase}-enabled`}>
      <span className="block mb-1 text-xs font-black uppercase tracking-tight italic text-[var(--app-text)]">{label}</span>
      <input
        type="text"
        value={value ?? ''}
        onChange={(e) => onChange?.(e.target.value)}
        className="w-full rounded-xl border border-[var(--input-border)] bg-[var(--input-bg)] px-3 py-2 text-sm text-[var(--app-text)]"
        data-testid={`${testidBase}-input`}
      />
    </label>
  );
}

export default FieldRow;
